import { eq, sql } from "@-/com.npmjs.drizzle-orm";
import type {
  ActorDatabaseHandle,
  ActorTransaction,
} from "@-/dev.improve.haniverse.database.actor";
import { createOrReviveActorRow } from "@-/dev.improve.haniverse.database.actor/create-or-revive";
import { actorSchema } from "@-/dev.improve.haniverse.database.schemas.actor.schema";
import { FREE_TIER_DAILY_NEW_QUEST_LIMIT } from "@-/dev.improve.haniverse.lib.entitlement.free-tier";
import { readHanWallet } from "@-/dev.improve.haniverse.lib.han-currency";
import {
  GLOBAL_QUEST_ADCODE,
  questScopeAdcode,
  type QuestScope,
} from "@-/dev.improve.haniverse.lib.quest.catalog";
import { deriveTravelState } from "@-/dev.improve.haniverse.lib.travel";
import { startOfLocalDay, startOfNextLocalDay } from "@-/dev.improve.haniverse.lib.time.local-day";
import { QuestError } from "#quest-error";
import { userQuestId } from "#user-quest-id";

/** XP paid for each successful practice run after the first completion. */
export const QUEST_PRACTICE_XP = 1;

/**
 * A city quest is only played in its city: not from elsewhere, not on the road. A global quest is
 * played wherever the learner is.
 */
async function requireOfferedHere(tx: ActorTransaction, scope: QuestScope, now: Date) {
  if (scope.kind === "global") {
    return;
  }
  const state = deriveTravelState(await tx.query.travelTrip.findMany({}), now);
  if (state.kind !== "at" || state.adcode !== scope.adcode) {
    throw new QuestError("not-here");
  }
}

/**
 * Unlocks a quest where it is offered, paying its `costHan` once. After that it can be attempted
 * any number of times. Unlocking an already unlocked quest does nothing, so a retry or a second
 * device never pays twice.
 */
export async function unlockQuest({
  actorDb,
  costHan,
  isPremium,
  now,
  questId,
  scope,
}: {
  actorDb: ActorDatabaseHandle;
  costHan: number;
  isPremium: boolean;
  now: Date;
  questId: string;
  scope: QuestScope;
}): Promise<void> {
  await actorDb.transaction(async (tx) => {
    const id = userQuestId(questId);
    if ((await tx.query.userQuest.findFirst({ where: { id } })) !== undefined) {
      return;
    }
    await requireOfferedHere(tx, scope, now);
    if (!isPremium) {
      const start = startOfLocalDay(now).toISOString();
      const end = startOfNextLocalDay(now).toISOString();
      const userQuests = await tx.query.userQuest.findMany({});
      const unlockedToday = userQuests.filter(
        (row) => row.unlockedAt >= start && row.unlockedAt < end,
      ).length;
      if (unlockedToday >= FREE_TIER_DAILY_NEW_QUEST_LIMIT) {
        throw new QuestError("daily-limit");
      }
    }
    const wallet = await readHanWallet(tx);
    if (wallet.han < costHan) {
      throw new QuestError("insufficient-han");
    }
    const nowIso = now.toISOString();
    await createOrReviveActorRow(tx, actorSchema.userQuest, {
      adcode: questScopeAdcode(scope),
      costHan,
      createdAt: nowIso,
      id,
      questId,
      unlockedAt: nowIso,
    });
  });
}

/**
 * Records a successful attempt at an unlocked quest. The first completion pays the quest's XP;
 * subsequent practice runs pay one XP each. Returns whether this call was the first completion.
 */
export async function completeQuest({
  actorDb,
  now,
  questId,
  xpEarned,
}: {
  actorDb: ActorDatabaseHandle;
  now: Date;
  questId: string;
  xpEarned: number;
}): Promise<boolean> {
  return actorDb.transaction(async (tx) => {
    const id = userQuestId(questId);
    const userQuest = await tx.query.userQuest.findFirst({ where: { id } });
    if (userQuest === undefined) {
      throw new QuestError("locked");
    }
    if (userQuest.completedAt !== null) {
      await tx
        .update(actorSchema.userQuest)
        .set({
          updatedAt: now.toISOString(),
          xpEarned: sql`${actorSchema.userQuest.xpEarned} + ${QUEST_PRACTICE_XP}`,
        })
        .where(eq(actorSchema.userQuest.id, id))
        .run();
      return false;
    }
    await requireOfferedHere(
      tx,
      userQuest.adcode === GLOBAL_QUEST_ADCODE
        ? { kind: "global" }
        : { adcode: userQuest.adcode, kind: "city" },
      now,
    );
    const nowIso = now.toISOString();
    await tx
      .update(actorSchema.userQuest)
      .set({ completedAt: nowIso, updatedAt: nowIso, xpEarned })
      .where(eq(actorSchema.userQuest.id, id))
      .run();
    return true;
  });
}
